import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import {
  campaignsApi, Campaign, CampaignKind, CampaignListParams, CampaignStatus, CampaignTag,
} from "../../../api/maintenance/campaigns";
import { money, num, ts, statusPill, errMsg, isPaused } from "./campaignUtils";
import { useNotice } from "../shared/useNotice";

interface Props {
  onOpen: (c: Campaign) => void;
  onNew: () => void;
  canManage: boolean;
  /** bumped by the console whenever a campaign changes, so the list refetches */
  refreshKey: number;
}

const PAGE = 50;

const KINDS: { key: "" | CampaignKind; label: string }[] = [
  { key: "", label: "All" },
  { key: "offer", label: "Cashback" },
  { key: "announcement", label: "Announcements" },
];

// "Ended" folds in cancelled: to an operator scanning the list they are both
// "over", and splitting them costs a chip to separate two and three rows.
const STATUSES: { key: string; label: string; counts: CampaignStatus[] }[] = [
  { key: "", label: "All", counts: [] },
  { key: "live", label: "Live", counts: ["live"] },
  { key: "scheduled", label: "Scheduled", counts: ["scheduled"] },
  { key: "draft", label: "Draft", counts: ["draft"] },
  { key: "ended,cancelled", label: "Ended", counts: ["ended", "cancelled"] },
];

const CampaignsTab: React.FC<Props> = ({ onOpen, onNew, canManage, refreshKey }) => {
  const [kind, setKind] = useState<"" | CampaignKind>("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");               // debounced copy of `search`
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState<Campaign[]>([]);
  const [counts, setCounts] = useState<{ kind: Record<string, number>; status: Record<string, number>; total: number }>(
    { kind: {}, status: {}, total: 0 });
  const [allTags, setAllTags] = useState<CampaignTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useNotice("error", "");

  // Typing should not fire a request per keystroke; 300ms is long enough to
  // finish a word and short enough not to feel laggy.
  useEffect(() => {
    const t = setTimeout(() => { setQ(search.trim()); setOffset(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Guards an out-of-order response overwriting a newer one: with a debounced
  // search two requests can be in flight and the slower one can land last.
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const params: CampaignListParams = { limit: PAGE, offset };
      if (kind) params.kind = kind;
      if (status) params.status = status;
      if (q) params.q = q;
      if (tags.length) params.tag = tags;
      if (from) params.from = new Date(from + "T00:00:00").toISOString();
      if (to) params.to = new Date(to + "T23:59:59").toISOString();
      const r = await campaignsApi.list(params);
      if (mine !== seq.current) return;
      setRows(r.data.campaigns);
      setCounts({ kind: r.data.counts.kind as any, status: r.data.counts.status as any, total: r.data.counts.total });
      setAllTags(r.data.tags || []);
      setErr("");
    } catch (e: any) {
      if (mine !== seq.current) return;
      setErr(errMsg(e, "Failed to load campaigns (has migration 033 run?)"));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [kind, status, q, tags, from, to, offset]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // "All" sums its own bucket rather than reading `counts.total`. The two are
  // not the same number: `total` is what matches every filter and drives the
  // pager, while a chip has to show what would match if that one chip were
  // switched — otherwise picking Cashback makes the Announcements chip read 0.
  const sum = (o: Record<string, number>) => Object.values(o).reduce((a, b) => a + b, 0);

  const statusCount = (s: { key: string; counts: CampaignStatus[] }) =>
    s.key === "" ? sum(counts.status) : s.counts.reduce((n, k) => n + (counts.status[k] || 0), 0);

  const kindCount = (k: "" | CampaignKind) => (k === "" ? sum(counts.kind) : counts.kind[k] || 0);

  const toggleTag = (t: string) => {
    setOffset(0);
    setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  };

  const filtered = !!(kind || status || q || tags.length || from || to);
  const clearAll = () => {
    setKind(""); setStatus(""); setSearch(""); setQ(""); setTags([]); setFrom(""); setTo(""); setOffset(0);
  };

  const chipRow = (
    items: { key: string; label: string; n: number }[],
    value: string,
    onPick: (k: any) => void,
  ) => (
    <div className="ma-presets">
      {items.map((i) => (
        <button key={i.key || "_all"} className={`ma-preset ${value === i.key ? "on" : ""}`}
          onClick={() => { onPick(i.key); setOffset(0); }}>
          {i.label}<span style={{ opacity: .65, marginLeft: 6 }}>{i.n}</span>
        </button>
      ))}
    </div>
  );

  /** An offer's rate card in one line; an announcement has none. */
  const rateBand = (c: Campaign): string => {
    if (c.min_payment_amount == null) return `${c.tier_count ?? 0} slot${(c.tier_count ?? 0) === 1 ? "" : "s"}`;
    const n = c.tier_count ?? 0;
    return `from ${money(c.min_payment_amount)} · ${n} slot${n === 1 ? "" : "s"}`;
  };

  const tagChips = (t: string[]) =>
    t.length ? (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
        {t.map((x) => <span key={x} className="ma-pill muted" style={{ fontSize: 10 }}>{x}</span>)}
      </div>
    ) : null;

  const row = (c: Campaign) => {
    const paused = isPaused(c);
    return (
      <tr key={c.id} className="clickable" onClick={() => onOpen(c)}>
        <td>
          <b>{c.name}</b>
          {/* The two kinds are different things and should not be scannable
              only by reading a word: an offer shows what it pays, an
              announcement shows what it says. */}
          <div className="sub">
            {c.kind === "offer"
              ? <>{rateBand(c)}{c.budget_cap ? ` · budget ${money(c.budget_cap)}` : ""}</>
              : <span style={{ fontStyle: "italic" }}>“{c.headline || c.subline || "no headline set"}”</span>}
          </div>
          {tagChips(c.tags || [])}
        </td>
        <td>
          <span className={`ma-pill ${c.kind === "offer" ? "info" : "muted"}`}>
            {c.kind === "offer" ? "cashback" : "announcement"}
          </span>
        </td>
        <td>
          <span className={`ma-pill ${statusPill(c.status)}`}>{c.status}</span>
          {paused && (
            <div className="sub" style={{ color: "#e65100", marginTop: 3 }}>
              paused{c.next_window ? ` · back ${ts(c.next_window.starts_at)}` : ""}
            </div>
          )}
        </td>
        <td className="sub">
          {ts(c.starts_at)}<br />→ {ts(c.ends_at)}
          {c.recurrence && <div style={{ color: "#5554d3" }}>on a schedule</div>}
        </td>
        <td className="num">{c.awarded_count ? <>{money(c.awarded_amount)}<div className="sub">{num(c.awarded_count)} rewards</div></> : "—"}</td>
        <td className="sub">{c.scheduled_by || c.created_by}</td>
      </tr>
    );
  };

  const shown = rows.length;
  const canPrev = offset > 0;
  const canNext = offset + PAGE < counts.total;

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: "0 0 2px", fontSize: 15 }}>All campaigns</h3>
          <div className="ma-hint">Nothing here can be edited once it has been scheduled. Change means: end this one, schedule the next.</div>
        </div>
        <span className="ma-spacer" />
        {canManage && <button className="ma-btn primary" onClick={onNew}>+ New campaign</button>}
      </div>

      {/* ---- filters */}
      <div className="ma-toolbar">
        {chipRow(KINDS.map((k) => ({ key: k.key, label: k.label, n: kindCount(k.key) })), kind, setKind)}
        {chipRow(STATUSES.map((s) => ({ key: s.key, label: s.label, n: statusCount(s) })), status, setStatus)}

        <label className="ma-select" style={{ minWidth: 200 }}>Search
          <span style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <Search size={13} style={{ position: "absolute", left: 8, color: "#8a92a1" }} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="name, slug or headline"
              style={{ border: "1px solid #e6e8ee", borderRadius: 7, padding: "6px 8px 6px 26px", fontSize: 13, width: "100%" }} />
          </span>
        </label>

        {/* Deliberately empty by default: the first question is usually "what is
            live", and a default month range hides it. */}
        <label className="ma-date">Ran on or after<input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setOffset(0); }} /></label>
        <label className="ma-date">and on or before<input type="date" value={to} onChange={(e) => { setTo(e.target.value); setOffset(0); }} /></label>

        {filtered && <button className="ma-btn" onClick={clearAll}><X size={13} /> Clear</button>}
      </div>

      {allTags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", margin: "0 0 12px" }}>
          <span className="ma-hint" style={{ marginRight: 2 }}>Tags</span>
          {allTags.map((t) => {
            const on = tags.includes(t.slug);
            // A tag's own colour wins when it has one; otherwise the standard
            // selected/unselected pills. Filtering is always by slug — the
            // label is display only and can be renamed without breaking a link.
            const tint = t.colour
              ? { background: on ? t.colour : "transparent", color: on ? "#fff" : t.colour, border: `1px solid ${t.colour}` }
              : undefined;
            return (
              <button key={t.slug} onClick={() => toggleTag(t.slug)} title={t.description || t.slug}
                className={`ma-pill ${t.colour ? "" : on ? "info" : "muted"}`}
                style={{ border: "none", cursor: "pointer", font: "inherit", fontSize: 11, fontWeight: 600, ...tint }}>
                {t.label || t.slug}
                <span style={{ opacity: .6, marginLeft: 5 }}>{t.usage_count}</span>
              </button>
            );
          })}
          {tags.length > 1 && <span className="ma-hint">showing campaigns with all {tags.length} tags</span>}
        </div>
      )}

      {err && <div className="ma-error">{err}</div>}

      <div className="ma-card">
        <div className="ma-tablewrap"><table className="ma-table">
          <thead><tr>
            <th>Campaign</th><th>Type</th><th>Status</th><th>Window</th><th className="num">Paid</th><th>Owner</th>
          </tr></thead>
          <tbody>
            {rows.map(row)}
            {!shown && (
              <tr><td colSpan={6} className="ma-empty">
                {loading ? "Loading…" : filtered ? "Nothing matches these filters." : "No campaigns yet"}
              </td></tr>
            )}
          </tbody>
        </table></div>

        {(canPrev || canNext) && (
          <div className="ma-card-b" style={{ display: "flex", alignItems: "center", gap: 10, borderTop: "1px solid #e6e8ee" }}>
            <span className="ma-hint">{offset + 1}–{offset + shown} of {num(counts.total)}</span>
            <span className="ma-spacer" />
            <button className="ma-btn" disabled={!canPrev} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</button>
            <button className="ma-btn" disabled={!canNext} onClick={() => setOffset(offset + PAGE)}>Next</button>
          </div>
        )}
      </div>
    </>
  );
};

export default CampaignsTab;
